// Owns the parsed Song, the edit history, and solo derivation. App
// supplies EditContext + SoloContext from this hook so the SoundLibrary
// (in the LeftRack) and the agent bridge can both apply edits through
// the same pipeline.
//
// Consumes:
//   - `code` / `onCodeChange`: external code buffer (App state)
//   - `soloId` / `onSoloChange`: solo lives in App so the play pipeline
//     can read it for isolated playback
//   - `onSelectSourceRange`: App forwards to the CodeMirror view

import { produce } from 'immer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generateCode } from '../blocks/codegen';
import type { EditOps, EditRecipe } from '../blocks/editContext';
import { parseCodeAsync } from '../blocks/parserClient';
import { computeSoloVisibility, soloIdStillExists } from '../blocks/solo';
import type { SoloOps } from '../blocks/soloContext';
import type { BlockId, ParseError, Song, SourceRange } from '../blocks/types';

const HISTORY_CAP = 100;

export type EditPhaseOk = {
  phase: 'ok';
  song: Song;
  history: Song[];
  future: Song[];
};

export type EditPhaseError = {
  phase: 'error';
  reason: string;
  location: ParseError['location'];
};

export type EditPhase = EditPhaseOk | EditPhaseError | { phase: 'initial' };

type Options = {
  code: string;
  onCodeChange: (next: string) => void;
  soloId: BlockId | null;
  onSoloChange: (next: BlockId | null) => void;
  onSelectSourceRange?: (range: SourceRange) => void;
};

export type SongEdit = {
  state: EditPhase;
  /** Currently-parsed song, or `null` if the buffer can't parse. Handy
   *  for callers that just want to read the model (tempo, voice count,
   *  …) without matching on phase. */
  song: Song | null;
  ops: EditOps;
  soloOps: SoloOps;
};

export function useSongEdit({
  code,
  onCodeChange,
  soloId,
  onSoloChange,
  onSelectSourceRange,
}: Options): SongEdit {
  const [state, setState] = useState<EditPhase>({ phase: 'initial' });
  const lastEmittedCodeRef = useRef<string>('');

  // External sync — reparse off-thread when the buffer changes from
  // outside our own `emit`. Cancellation guards against out-of-order
  // resolution when the user types fast.
  useEffect(() => {
    if (code === lastEmittedCodeRef.current) return;
    let cancelled = false;
    parseCodeAsync(code).then((result) => {
      if (cancelled) return;
      if (result.ok) {
        setState({ phase: 'ok', song: result.song, history: [], future: [] });
      } else {
        setState({
          phase: 'error',
          reason: result.reason,
          location: result.location,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [code]);

  const emit = useCallback(
    (nextSong: Song) => {
      const nextCode = generateCode(nextSong);
      lastEmittedCodeRef.current = nextCode;
      onCodeChange(nextCode);
    },
    [onCodeChange],
  );

  const update = useCallback(
    (recipe: EditRecipe) => {
      setState((prev) => {
        if (prev.phase !== 'ok') return prev;
        const next = produce(prev.song, recipe);
        if (next === prev.song) return prev;
        emit(next);
        return {
          phase: 'ok',
          song: next,
          history: [...prev.history, prev.song].slice(-HISTORY_CAP),
          future: [],
        };
      });
    },
    [emit],
  );

  const undo = useCallback(() => {
    setState((prev) => {
      if (prev.phase !== 'ok' || prev.history.length === 0) return prev;
      const history = prev.history.slice();
      const previous = history.pop();
      if (!previous) return prev;
      emit(previous);
      return {
        phase: 'ok',
        song: previous,
        history,
        future: [prev.song, ...prev.future].slice(0, HISTORY_CAP),
      };
    });
  }, [emit]);

  const redo = useCallback(() => {
    setState((prev) => {
      if (prev.phase !== 'ok' || prev.future.length === 0) return prev;
      const [next, ...restFuture] = prev.future;
      if (!next) return prev;
      emit(next);
      return {
        phase: 'ok',
        song: next,
        history: [...prev.history, prev.song].slice(-HISTORY_CAP),
        future: restFuture,
      };
    });
  }, [emit]);

  const selectSourceRange = useCallback(
    (range: SourceRange) => {
      onSelectSourceRange?.(range);
    },
    [onSelectSourceRange],
  );

  const revealBlock = useCallback((id: string) => {
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(
        `[data-block-id="${CSS.escape(id)}"]`,
      );
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }, []);

  const historyLen = state.phase === 'ok' ? state.history.length : 0;
  const futureLen = state.phase === 'ok' ? state.future.length : 0;
  const ops = useMemo<EditOps>(
    () => ({
      update,
      undo,
      redo,
      canUndo: historyLen > 0,
      canRedo: futureLen > 0,
      selectSourceRange,
      revealBlock,
    }),
    [update, undo, redo, selectSourceRange, revealBlock, historyLen, futureLen],
  );

  const song = state.phase === 'ok' ? state.song : null;

  // Drop a stale solo — user deleted the block or an external edit
  // changed shape so the id no longer exists.
  useEffect(() => {
    if (soloId && song && !soloIdStillExists(song, soloId)) {
      onSoloChange(null);
    }
  }, [soloId, song, onSoloChange]);

  const visibleIds = useMemo(
    () => (song && soloId ? computeSoloVisibility(song, soloId) : new Set<BlockId>()),
    [song, soloId],
  );

  const soloOps = useMemo<SoloOps>(
    () => ({
      soloId,
      visibleIds,
      toggle: (id) => onSoloChange(soloId === id ? null : id),
      clear: () => onSoloChange(null),
    }),
    [soloId, visibleIds, onSoloChange],
  );

  return { state, song, ops, soloOps };
}
